import VindiClient from './vindiClient';
import { detectCardBrand } from '../utils/cardBrand';

/**
 * Configuração de ambiente da API Vindi
 */
const isSandbox = process.env.REACT_APP_VINDI_SANDBOX !== 'false';

/**
 * Cliente para tokenização (usa chave pública)
 */
const vindiPublicKey = process.env.REACT_APP_VINDI_PUBLIC_KEY;

// Debug: Verifica se a chave pública está configurada
if (process.env.NODE_ENV === 'development') {
  console.log('[Vindi Setup] Public Key configured:', vindiPublicKey ? 'YES' : 'NO');
  console.log('[Vindi Setup] Sandbox mode:', isSandbox);
}

const publicClient = new VindiClient(
  vindiPublicKey,
  isSandbox
);

/**
 * Cliente para operações autenticadas (usa chave privada)
 */
const privateClient = new VindiClient(
  process.env.REACT_APP_VINDI_API_KEY,
  isSandbox
);

/**
 * Serviço de API da Vindi
 *
 * Fornece métodos para tokenização de cartões, gestão de clientes,
 * assinaturas e faturas através da API Vindi
 */
class VindiApiService {
  /**
   * TOKENIZAÇÃO DE CARTÕES (Checkout Transparente)
   */

  /**
   * Tokeniza um cartão de crédito
   * @param {Object} cardData - Dados do cartão
   * @param {string} cardData.holder_name - Nome do portador do cartão
   * @param {string} cardData.registry_code - CPF ou CNPJ do portador
   * @param {string} cardData.card_number - Número do cartão
   * @param {string} cardData.card_cvv - Código CVV
   * @param {string} cardData.card_expiration - Data de expiração (MM/YYYY)
   * @param {boolean} [cardData.allow_as_fallback=true] - Permite usar em retentativas
   * @param {string} [cardData.payment_company_code] - Código da bandeira (auto-detectado se não fornecido)
   * @returns {Promise<Object>} Resposta com o token do cartão e payment_profile
   */
  async tokenizeCard(cardData) {
    try {
      // Detecta a bandeira automaticamente se não fornecida
      const paymentCompanyCode = cardData.payment_company_code ||
        detectCardBrand(cardData.card_number);

      if (!paymentCompanyCode) {
        throw new Error('Não foi possível identificar a bandeira do cartão');
      }

      const payload = {
        holder_name: cardData.holder_name,
        registry_code: cardData.registry_code?.replace(/\D/g, ''), // Remove formatação
        card_expiration: cardData.card_expiration,
        card_number: cardData.card_number?.replace(/\D/g, ''), // Remove espaços
        card_cvv: cardData.card_cvv,
        payment_method_code: 'credit_card',
        payment_company_code: paymentCompanyCode,
        allow_as_fallback: cardData.allow_as_fallback ?? true,
      };

      const response = await publicClient.post('/public/payment_profiles', payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao tokenizar cartão:', error);
      throw this.handleError(error);
    }
  }

  /**
   * GESTÃO DE CLIENTES
   */

  /**
   * Verifica se um cliente existe através do CPF/CNPJ
   * @param {string} registryCode - CPF ou CNPJ do cliente
   * @returns {Promise<Object>} Dados do cliente ou null se não encontrado
   */
  async findCustomerByRegistryCode(registryCode) {
    try {
      const cleanCode = registryCode.replace(/\D/g, '');
      const response = await privateClient.get('/customers', {
        params: {
          query: `registry_code=${cleanCode}`,
        },
      });

      const customers = response.data?.customers || [];
      return customers.length > 0 ? customers[0] : null;
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cria um novo cliente
   * @param {Object} customerData - Dados do cliente
   * @returns {Promise<Object>} Dados do cliente criado
   */
  async createCustomer(customerData) {
    try {
      const payload = {
        name: customerData.name,
        email: customerData.email,
        registry_code: customerData.registry_code?.replace(/\D/g, ''),
        code: customerData.code,
        notes: customerData.notes,
        metadata: customerData.metadata,
        address: customerData.address,
        phones: customerData.phones,
      };

      const response = await privateClient.post('/customers', payload);
      return response.data?.customer;
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Atualiza um cliente existente
   * @param {number|string} customerId - ID do cliente
   * @param {Object} customerData - Dados para atualização
   * @returns {Promise<Object>} Dados do cliente atualizado
   */
  async updateCustomer(customerId, customerData) {
    try {
      const response = await privateClient.put(`/customers/${customerId}`, customerData);
      return response.data?.customer;
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      throw this.handleError(error);
    }
  }

  /**
   * GESTÃO DE ASSINATURAS
   */

  /**
   * Cria uma nova assinatura
   * @param {Object} subscriptionData - Dados da assinatura
   * @returns {Promise<Object>} Dados da assinatura criada
   */
  async createSubscription(subscriptionData) {
    try {
      const payload = {
        plan_id: subscriptionData.plan_id,
        customer_id: subscriptionData.customer_id,
        payment_method_code: subscriptionData.payment_method_code || 'credit_card',
        code: subscriptionData.code,
        installments: subscriptionData.installments,
        product_items: subscriptionData.product_items,
        metadata: subscriptionData.metadata,
        start_at: subscriptionData.start_at,
        payment_profile: subscriptionData.payment_profile,
      };

      const response = await privateClient.post('/subscriptions', payload);
      return response.data?.subscription;
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancela uma assinatura
   * @param {number|string} subscriptionId - ID da assinatura
   * @param {boolean} cancelBills - Se deve cancelar faturas pendentes
   * @returns {Promise<Object>} Confirmação do cancelamento
   */
  async cancelSubscription(subscriptionId, cancelBills = true) {
    try {
      const response = await privateClient.delete(`/subscriptions/${subscriptionId}`, {
        data: { cancel_bills: cancelBills },
      });
      return response.data?.subscription;
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * GESTÃO DE FATURAS AVULSAS
   */

  /**
   * Cria uma fatura avulsa
   * @param {Object} billData - Dados da fatura
   * @returns {Promise<Object>} Dados da fatura criada
   */
  async createBill(billData) {
    try {
      const payload = {
        customer_id: billData.customer_id,
        code: billData.code,
        payment_method_code: billData.payment_method_code || 'credit_card',
        installments: billData.installments,
        bill_items: billData.bill_items,
        metadata: billData.metadata,
        payment_profile: billData.payment_profile,
      };

      const response = await privateClient.post('/bills', payload);
      return response.data?.bill;
    } catch (error) {
      console.error('Erro ao criar fatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancela uma fatura
   * @param {number|string} billId - ID da fatura
   * @returns {Promise<Object>} Confirmação do cancelamento
   */
  async cancelBill(billId) {
    try {
      const response = await privateClient.delete(`/bills/${billId}`);
      return response.data?.bill;
    } catch (error) {
      console.error('Erro ao cancelar fatura:', error);
      throw this.handleError(error);
    }
  }

  /**
   * GESTÃO DE ESTORNOS
   */

  /**
   * Realiza estorno de uma cobrança
   * @param {number|string} chargeId - ID da cobrança
   * @param {Object} refundData - Dados do estorno
   * @param {number} [refundData.amount] - Valor a estornar (total se não informado)
   * @param {boolean} [refundData.cancel_bill=true] - Se deve cancelar a fatura
   * @param {string} [refundData.comments] - Comentários sobre o estorno
   * @returns {Promise<Object>} Confirmação do estorno
   */
  async refundCharge(chargeId, refundData = {}) {
    try {
      const payload = {
        amount: refundData.amount,
        cancel_bill: refundData.cancel_bill ?? true,
        comments: refundData.comments,
      };

      const response = await privateClient.post(`/charges/${chargeId}/refund`, payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao estornar cobrança:', error);
      throw this.handleError(error);
    }
  }

  /**
   * UTILITÁRIOS
   */

  /**
   * Trata erros da API e retorna objeto padronizado
   * @param {Error} error - Erro capturado
   * @returns {Object} Objeto de erro padronizado
   */
  handleError(error) {
    if (error.response) {
      // Erro da resposta HTTP da Vindi
      const vindiError = error.response.data;

      // Novo formato: array direto de erros
      // [{ "id": "invalid_parameter", "parameter": "card_expiration", "message": "não deve estar expirado" }]
      if (Array.isArray(vindiError)) {
        const errorMessages = vindiError.map(err => {
          if (err.parameter && err.message) {
            return `${err.parameter}: ${err.message}`;
          }
          return err.message || 'Erro desconhecido';
        });

        return {
          type: 'VINDI_API_ERROR',
          status: error.response.status,
          message: errorMessages.join('; '),
          errors: vindiError,
          details: vindiError,
        };
      }

      // Formato antigo: objeto com campo errors
      return {
        type: 'VINDI_API_ERROR',
        status: error.response.status,
        message: vindiError?.errors?.[0] || vindiError?.message || 'Erro na API Vindi',
        errors: vindiError?.errors || [],
        details: vindiError,
      };
    } else if (error.request) {
      // Erro de rede
      return {
        type: 'NETWORK_ERROR',
        message: 'Erro de conexão com a Vindi',
        details: error.request,
      };
    } else {
      // Erro de configuração ou outro
      return {
        type: 'UNKNOWN_ERROR',
        message: error.message || 'Erro desconhecido',
        details: error,
      };
    }
  }

  /**
   * Método genérico para requisições customizadas
   * @param {string} method - Método HTTP
   * @param {string} url - URL do endpoint
   * @param {Object} data - Dados da requisição
   * @param {boolean} usePrivateKey - Se deve usar chave privada (default: true)
   * @returns {Promise<Object>} Resposta da API
   */
  async customRequest(method, url, data = null, usePrivateKey = true) {
    try {
      const client = usePrivateKey ? privateClient : publicClient;
      const config = {
        method,
        url,
        ...(data && { data }),
      };
      const response = await client.request(config);
      return response.data;
    } catch (error) {
      console.error('Erro em requisição customizada:', error);
      throw error;
    }
  }
}

// Exportar instância singleton do serviço
const vindiApiService = new VindiApiService();

export default vindiApiService;

// Exportar também a classe para casos onde múltiplas instâncias sejam necessárias
export { VindiApiService, publicClient, privateClient };
